<template>
  <div class="container-modal-upsell">
    <div class="wrapper-plans">
      <Tag type="gray-light" :label="t('self_service_plan_subscribed_to', { plan: planNow })" />

      <div class="wrapper-upgrade">
        <AnimationAi />

        <h4>
          <i18n-t keypath="ss_upsell_updgrade_to" scope="global">
            <template #planAfter>
              <span class="primary accent">{{ planAfter }}</span>
            </template>
          </i18n-t>
        </h4>
      </div>
    </div>
    <p>{{ t('upgrade_immediately_body_1') }}</p>
  </div>

  <div class="footer">
    <ButtonProcess secondary @click="handleCreditPurchase">
      {{ t('buy_credits') }}
    </ButtonProcess>

    <ButtonProcess full-width :loading="isLoading" @click="handleUpgrade">
      {{ t('upgrade_immediately') }}
    </ButtonProcess>
  </div>
</template>

<script setup lang="ts">
import { capitalize, camelCase } from 'lodash';

import AnimationAi from '@/components/animations/AnimationAi.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import Tag from '@/components/shared/base/Tag.vue';
import { useGtag } from '@/composables/shared/useGtag';
import { usePayment } from '@/composables/shared/usePayment';
import { usePaymentStore } from '@/stores/payment';
import { usePopupStore } from '@/stores/popup';
import { useSubscriptionStore } from '@/stores/subscription';

const { t } = useI18n();

const paymentStore = usePaymentStore();
const subscriptionStore = useSubscriptionStore();
const popupStore = usePopupStore();

const {
  hasRegisteredCard,
  openCardRegisterPopup,
  openCardManagePopup,
  openSubscriptionPaymentPopup,
  openCreditBuyPopup,
} = usePayment();
const { trackEventFunnel } = useGtag();

const isLoading = ref<boolean>(false);

const planNow = capitalize(camelCase(subscriptionStore.currentPlan));
const planAfter = subscriptionStore.currentPlan === 'FREE' ? 'Starter' : 'Pro';

const handleUpgrade = async () => {
  if (isLoading.value) {
    return;
  }

  isLoading.value = true;

  trackEventFunnel('click_plan_upgrade_button');

  if (subscriptionStore.currentPlan === 'FREE') {
    closePlanUpgradePopup();

    if (!hasRegisteredCard()) {
      const registeredCardType = await openCardRegisterPopup();
      if (!registeredCardType) {
        return;
      }
    }

    const selectedCardType = await openCardManagePopup('PAYMENT', subscriptionStore.paymentType);
    if (!selectedCardType) {
      return;
    }

    openSubscriptionPaymentPopup('STARTER', 'MONTHLY');
  } else {
    closePlanUpgradePopup();
    openSubscriptionPaymentPopup('PRO', subscriptionStore.currentPlanDetails?.billingCycle);
  }

  isLoading.value = false;
};

const handleCreditPurchase = async () => {
  trackEventFunnel('click_credit_buy_button');

  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      return;
    }
  }

  if (subscriptionStore.lastPaymentErrorCode) {
    const selectedCardType = await openCardManagePopup('PAYMENT', subscriptionStore.paymentType);
    if (!selectedCardType) {
      return;
    }
  }

  closePlanUpgradePopup();
  openCreditBuyPopup();
};

const closePlanUpgradePopup = () => {
  popupStore.closeLast();
};

onMounted(async () => {
  await paymentStore.fetchUserCardList();
});
</script>

<style scoped>
.container-modal-upsell {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.wrapper-plans {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.wrapper-upgrade {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.footer {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  justify-content: flex-end;
}
</style>
